/**
 * 평면 모양 편집: 직사각형에서 잘라낸 부분(모서리 잘라내기 / 변의 홈) 목록
 * ㄱ자 = 모서리 하나, ㄷ자 = 변 가운데 홈, 계단형 = 모서리 + 옆에 붙은 홈
 */
import React from 'react';
import { Notch, Corner, Side, rectWithNotches } from '../engine/polyEngine';

interface Props {
  H: number; V: number;
  cuts: Notch[];
  onChange: (cuts: Notch[]) => void;
}

const CORNER_NAME: Record<Corner, string> = { topLeft: '좌상', topRight: '우상', bottomLeft: '좌하', bottomRight: '우하' };
const SIDE_NAME: Record<Side, string> = { top: '상단', right: '우측', bottom: '하단', left: '좌측' };
const r100 = (v: number) => Math.round(v / 100) * 100;

/** 잘라낸 부분 검증: 범위·겹침 (문제 문구 목록) */
export function validateCuts(H: number, V: number, cuts: Notch[]): string[] {
  const errs: string[] = [];
  const boxes = cuts.map(c => {
    if (c.type === 'corner') {
      const x0 = c.corner.endsWith('Left') ? 0 : H - c.w, y0 = c.corner.startsWith('top') ? 0 : V - c.h;
      return [x0, y0, x0 + c.w, y0 + c.h];
    }
    const along = c.side === 'top' || c.side === 'bottom';
    const [a0, a1] = [Math.min(c.from, c.to), Math.max(c.from, c.to)];
    if (along) return [a0, c.side === 'top' ? 0 : V - c.depth, a1, c.side === 'top' ? c.depth : V];
    return [c.side === 'left' ? 0 : H - c.depth, a0, c.side === 'left' ? c.depth : H, a1];
  });
  boxes.forEach((b, i) => {
    const name = `${i + 1}번`;
    if (b[2] - b[0] < 300 || b[3] - b[1] < 300) errs.push(`${name}: 300mm 이상이어야 함`);
    if (b[0] < 0 || b[1] < 0 || b[2] > H || b[3] > V) errs.push(`${name}: 도면 밖`);
    if (b[2] - b[0] > H - 1000 || b[3] - b[1] > V - 1000) errs.push(`${name}: 너무 큼 (남는 폭 1000mm 이상 필요)`);
    for (let k = i + 1; k < boxes.length; k++) {
      const o = boxes[k];
      if (Math.min(b[2], o[2]) - Math.max(b[0], o[0]) > 0 && Math.min(b[3], o[3]) - Math.max(b[1], o[1]) > 0) errs.push(`${name}·${k + 1}번 겹침`);
    }
  });
  return errs;
}

const ShapeEditor: React.FC<Props> = ({ H, V, cuts, onChange }) => {
  const presets: [string, () => Notch[]][] = [
    ['직사각형', () => []],
    ['ㄱ자', () => [{ type: 'corner', corner: 'topRight', w: r100(H * 0.4), h: r100(V * 0.4), kind: 'wall' }]],
    ['ㄷ자', () => [{ type: 'side', side: 'top', from: r100(H * 0.3), to: r100(H * 0.7), depth: r100(V * 0.4), kind: 'wall' }]],
    ['계단형', () => [
      { type: 'corner', corner: 'topRight', w: r100(H * 0.3), h: r100(V * 0.3), kind: 'wall' },
      { type: 'side', side: 'top', from: r100(H * 0.45), to: r100(H * 0.7), depth: r100(V * 0.15), kind: 'wall' },
    ]],
  ];
  const set = (i: number, patch: Partial<Notch>) => onChange(cuts.map((c, k) => (k === i ? ({ ...c, ...patch } as Notch) : c)));
  const errs = validateCuts(H, V, cuts);

  // 미리보기 (SVG)
  const outline = rectWithNotches(H, V, cuts);
  const W = 220, k = Math.min(W / H, 140 / V);
  const path = outline.pts.map((p, i) => `${i ? 'L' : 'M'}${(p.x * k).toFixed(1)},${(p.y * k).toFixed(1)}`).join(' ') + ' Z';

  const num = (v: number, on: (n: number) => void) => (
    <input type="number" value={v} step={100} onChange={e => on(Number(e.target.value) || 0)}
      className="pd-input sm" style={{ width: 66 }} />
  );

  return (
    <div className="pd-card" style={{ marginTop: 10 }}>
      <h3 className="pd-title">평면 모양</h3>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {presets.map(([name, make]) => (
          <button key={name} onClick={() => onChange(make())} className="pd-btn sm" style={{ flex: 1, padding: 0 }}>{name}</button>
        ))}
      </div>
      <svg width={H * k} height={V * k} style={{ display: 'block', margin: '0 auto 10px', overflow: 'visible' }}>
        <path d={path} fill="#E8F3FF" stroke="#3182F6" strokeWidth={1.5} strokeLinejoin="round" />
      </svg>
      {cuts.map((c, i) => (
        <div key={i} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--pd-sub)', padding: '8px 0', borderTop: '1px solid var(--pd-fill)' }}>
          <b style={{ width: 22 }}>{i + 1}</b>
          {c.type === 'corner' ? (<>
            <select value={c.corner} onChange={e => set(i, { corner: e.target.value as Corner })} className="pd-input sm" style={{ width: 'auto' }}>
              {(Object.keys(CORNER_NAME) as Corner[]).map(x => <option key={x} value={x}>{CORNER_NAME[x]} 모서리</option>)}
            </select>
            가로 {num(c.w, n => set(i, { w: n }))} 세로 {num(c.h, n => set(i, { h: n }))}
          </>) : (<>
            <select value={c.side} onChange={e => set(i, { side: e.target.value as Side })} className="pd-input sm" style={{ width: 'auto' }}>
              {(Object.keys(SIDE_NAME) as Side[]).map(x => <option key={x} value={x}>{SIDE_NAME[x]} 홈</option>)}
            </select>
            {num(c.from, n => set(i, { from: n }))}~{num(c.to, n => set(i, { to: n }))} 깊이 {num(c.depth, n => set(i, { depth: n }))}
          </>)}
          <button onClick={() => onChange(cuts.filter((_, k2) => k2 !== i))}
            className="pd-del" style={{ marginLeft: 'auto' }}>✕</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button onClick={() => onChange([...cuts, { type: 'corner', corner: 'topRight', w: r100(H * 0.3), h: r100(V * 0.3), kind: 'wall' }])}
          className="pd-btn weak sm" style={{ flex: 1 }}>+ 모서리 자르기</button>
        <button onClick={() => onChange([...cuts, { type: 'side', side: 'bottom', from: r100(H * 0.4), to: r100(H * 0.6), depth: r100(V * 0.25), kind: 'wall' }])}
          className="pd-btn weak sm" style={{ flex: 1 }}>+ 홈 추가</button>
      </div>
      {errs.length > 0 && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--pd-red)', fontWeight: 600 }}>⚠ {errs.join(' / ')} — 고칠 때까지 직사각형으로 그림</div>}
      <div className="pd-hint" style={{ marginTop: 8 }}>기존 벽(패널 없음)은 도구 막대 <b>기존벽</b>으로 변을 누르세요</div>
    </div>
  );
};

export default ShapeEditor;
